import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, LogOut, Menu, X } from 'lucide-react';
import NotificationsBell from './NotificationsBell';
import '../../styles/authenticated.css';

const AuthenticatedLayout = ({ user, activeTab, onTabChange, navItems, children, role, pageTitle }) => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);


  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      localStorage.removeItem('access_token');
      navigate('/login');
    } catch (error) {
      console.error('Logout failed');
    }
  };

  const handleNavClick = (tab) => {
    onTabChange(tab);
    setSidebarOpen(false);
  };

  const userInitials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  const roleLabels = {
    client: 'Plaintiff',
    lawyer: 'Attorney',
    funder: 'Funder',
    admin: 'Administrator',
    growth_partner: 'Growth Partner'
  };

  return (
    <div className="mcl-app" data-testid="authenticated-layout">
      {/* Sidebar overlay for mobile */}
      <div
        className={`mcl-sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(false)}
        data-testid="sidebar-overlay"
      />

      {/* Sidebar */}
      <aside className={`mcl-sidebar ${sidebarOpen ? 'open' : ''}`} data-testid="sidebar">
        {/* Brand */}
        <div className="mcl-sidebar-brand">
          <Scale size={24} className="mcl-sidebar-brand-icon" />
          <span className="mcl-sidebar-brand-name">MyCaseLoan</span>
        </div>

        {/* Navigation */}
        <nav className="mcl-sidebar-nav" data-testid={`${role}-nav`}>
          <div className="mcl-sidebar-label">Main Menu</div>
          {navItems.map((item) => (
            <button
              key={item.id}
              className={`mcl-sidebar-item ${activeTab === item.id ? 'active' : ''}`}
              onClick={() => handleNavClick(item.id)}
              data-testid={`nav-${item.id}`}
            >
              <item.icon size={20} className="sidebar-icon" />
              <span>{item.label}</span>
              {/* Optional per-item count. Undefined on every dashboard that
                  does not set one, so those sidebars are unchanged. */}
              {item.badge ? (
                <span className="mcl-sidebar-badge" data-testid={`nav-badge-${item.id}`}>{item.badge}</span>
              ) : null}
            </button>
          ))}
        </nav>

        {/* Footer with user info */}
        <div className="mcl-sidebar-footer">
          <div className="mcl-sidebar-user">
            <div className="mcl-sidebar-avatar" data-testid="user-avatar">
              {userInitials}
            </div>
            <div className="mcl-sidebar-user-info">
              <div className="mcl-sidebar-user-name" data-testid="user-name">{user?.name || 'Loading...'}</div>
              <div className="mcl-sidebar-user-role">{roleLabels[role] || role}</div>
            </div>
            <button
              className="mcl-sidebar-logout"
              onClick={handleLogout}
              title="Logout"
              data-testid="logout-button"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div className="mcl-main-wrapper">
        {/* Topbar */}
        <header className="mcl-topbar" data-testid="topbar">
          <div className="mcl-topbar-left">
            <button
              className="mcl-mobile-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            <h2 className="mcl-topbar-title" data-testid="topbar-title">{pageTitle || ''}</h2>
          </div>
          <div className="mcl-topbar-right">
            <NotificationsBell user={user} />
            <div className="d-none d-lg-flex align-items-center" style={{ gap: '0.5rem', marginLeft: '0.5rem' }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--mcl-text-body)' }}>{user?.name}</span>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="mcl-content" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AuthenticatedLayout;
